import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Heading,
  Badge,
  IconButton,
  Card,
  CardBody,
  useColorModeValue,
  useToast,
  Select,
  Button,
} from '@chakra-ui/react';
import { 
  FiBell, 
  FiTrash2, 
  FiCheck,
  FiRefreshCw,
  FiAlertCircle,
  FiDollarSign,
  FiStar,
} from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import {
  getNotifications,
  markNotificationAsRead,
  deleteNotification,
  markAllNotificationsAsRead,
} from '../services/api';
import { useSelector } from 'react-redux';

const NotificationCard = ({ notification, onMarkAsRead, onDelete }) => {
  const bgColor = useColorModeValue('white', 'gray.700');
  const unreadBgColor = useColorModeValue('blue.50', 'blue.900');

  const getIcon = (type) => {
    switch (type) {
      case 'system_update':
        return <FiRefreshCw />;
      case 'security_alert':
        return <FiAlertCircle />;
      case 'plan_renewal':
        return <FiDollarSign />;
      case 'usage_alert':
        return <FiAlertCircle />;
      case 'feature_update':
        return <FiStar />;
      default:
        return <FiBell />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent':
        return 'red';
      case 'high':
        return 'orange';
      case 'medium':
        return 'yellow';
      case 'low':
        return 'blue';
      default:
        return 'gray';
    }
  };

  return (
    <Card
      bg={notification.read ? bgColor : unreadBgColor}
      boxShadow="md"
      mb={4}
      transition="all 0.2s"
      _hover={{ transform: 'translateY(-2px)', shadow: 'lg' }}
    >
      <CardBody>
        <VStack align="stretch" spacing={3}>
          <HStack justify="space-between">
            <HStack>
              {getIcon(notification.type)}
              <Heading size="sm">{notification.title}</Heading>
            </HStack>
            <Badge colorScheme={getPriorityColor(notification.priority)}>
              {notification.priority}
            </Badge>
          </HStack>
          
          <Text>{notification.message}</Text>
          
          <HStack justify="space-between" fontSize="sm" color="gray.500">
            <Text>{new Date(notification.createdAt).toLocaleString()}</Text>
            <HStack>
              {!notification.read && (
                <IconButton
                  icon={<FiCheck />}
                  size="sm"
                  variant="ghost"
                  onClick={() => onMarkAsRead(notification._id)}
                  aria-label="Mark as read"
                />
              )}
              <IconButton
                icon={<FiTrash2 />}
                size="sm"
                variant="ghost"
                colorScheme="red"
                onClick={() => onDelete(notification._id)}
                aria-label="Delete notification"
              />
            </HStack>
          </HStack>
        </VStack>
      </CardBody>
    </Card>
  );
};

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [filter, setFilter] = useState('all');
  const toast = useToast();
  const { user: currentUser } = useSelector((state) => state.auth);

  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    try {
      const data = await getNotifications(currentUser?.role === 'admin' || currentUser?.role === 'superadmin' ? null : currentUser?._id);
      setNotifications(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading notifications:', error);
      setNotifications([]);
      toast({
        title: 'Error',
        description: 'Failed to load notifications',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleMarkAsRead = async (id) => {
    if (!id) return;
    try {
      await markNotificationAsRead(id);
      setNotifications(notifications.map(n => 
        n._id === id ? { ...n, read: true } : n
      ));
    } catch (error) {
      console.error('Error marking notification as read:', error);
      toast({
        title: 'Error',
        description: 'Failed to mark notification as read',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleDelete = async (id) => {
    if (!id) return;
    try {
      await deleteNotification(id);
      setNotifications(notifications.filter(n => n._id !== id));
    } catch (error) {
      console.error('Error deleting notification:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete notification',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllNotificationsAsRead();
      setNotifications(notifications.map(n => ({ ...n, read: true })));
      toast({
        title: 'Success',
        description: 'All notifications marked as read',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      toast({
        title: 'Error',
        description: 'Failed to mark all notifications as read',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const filteredNotifications = Array.isArray(notifications) 
    ? notifications.filter(n => filter === 'all' || n.type === filter)
    : [];

  return (
    <Box minH="100vh" bg={useColorModeValue('gray.50', 'gray.800')} p={6}>
      <VStack spacing={6} align="stretch">
        <HStack justify="space-between">
          <Heading size="xl">Notifications</Heading>
          <HStack>
            <Select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              width="200px"
            >
              <option value="all">All Notifications</option>
              <option value="system_update">System Updates</option>
              <option value="plan_renewal">Plan Renewals</option>
              <option value="usage_alert">Usage Alerts</option>
              <option value="feature_update">Feature Updates</option>
            </Select>
            <Button
              leftIcon={<FiCheck />}
              onClick={handleMarkAllAsRead}
              isDisabled={!notifications.some(n => !n.read)}
            >
              Mark All as Read
            </Button>
          </HStack>
        </HStack>
        
        {filteredNotifications.length === 0 ? (
          <Box textAlign="center" py={10}>
            <Text color="gray.500">No notifications found</Text>
          </Box>
        ) : (
          filteredNotifications.map(notification => (
            <NotificationCard
              key={notification._id}
              notification={notification}
              onMarkAsRead={handleMarkAsRead}
              onDelete={handleDelete}
            />
          ))
        )}
      </VStack>
    </Box>
  );
};

export default Notifications; 